import type { AddRegistrationRequest, AnnouncementRequest, OkResponse, PersonalTokenResponse, RequestDecision, TokenAction, UpdateContractorRequest } from '@crew/shared';
import type { User } from 'firebase/auth';
import { DEMO_ME, demoStore } from '@/demo/demoStore';
import { IS_DEMO } from '@/demo/demoMode';
import { apiRequest } from './api';

/** פעולות מנהל: נרשמים, בקשות שינוי, מאגר, התראות ומכשירים. */
export interface AdminApi {
  addRegistration(jobId: string, body: AddRegistrationRequest): Promise<PersonalTokenResponse>;
  updateCount(jobId: string, cid: string, workerCount: number): Promise<OkResponse>;
  removeRegistration(jobId: string, cid: string): Promise<OkResponse>;
  tokenAction(jobId: string, cid: string, action: TokenAction): Promise<{ personalToken?: string }>;
  decide(requestId: string, decision: RequestDecision): Promise<{ status: string }>;
  updateContractor(cid: string, body: UpdateContractorRequest): Promise<OkResponse>;
  markRead(ids: string[] | 'all'): Promise<unknown>;
  registerDevice(token: string): Promise<OkResponse>;
  unregisterDevice(token: string): Promise<OkResponse>;
  createAnnouncement(jobId: string, body: AnnouncementRequest): Promise<{ id: string }>;
  updateAnnouncement(jobId: string, id: string, body: AnnouncementRequest): Promise<OkResponse>;
  deleteAnnouncement(jobId: string, id: string): Promise<OkResponse>;
  sendThreadMessage(threadId: string, text: string): Promise<unknown>;
  markThreadRead(threadId: string): Promise<unknown>;
  sendTeamMessage(text: string): Promise<unknown>;
  markTeamRead(): Promise<unknown>;
}

const enc = encodeURIComponent;

export function adminApi(user: User): AdminApi {
  if (IS_DEMO) {
    return {
      addRegistration: (j, b) => demoStore.addRegistration(j, b),
      updateCount: (j, c, n) => demoStore.updateCount(j, c, n),
      removeRegistration: (j, c) => demoStore.removeRegistration(j, c),
      tokenAction: (j, c, a) => demoStore.tokenAction(j, c, a),
      decide: (id, d) => demoStore.decide(id, d),
      updateContractor: (c, b) => demoStore.updateContractor(c, b),
      markRead: (ids) => demoStore.markRead(ids),
      registerDevice: async () => ({ ok: true }),
      unregisterDevice: async () => ({ ok: true }),
      createAnnouncement: (j, b) => demoStore.createAnnouncement(j, b),
      updateAnnouncement: (j, id, b) => demoStore.updateAnnouncement(j, id, b),
      deleteAnnouncement: (j, id) => demoStore.deleteAnnouncement(j, id),
      sendThreadMessage: (t, text) => demoStore.adminSend(t, text),
      markThreadRead: (t) => demoStore.adminRead(t),
      sendTeamMessage: (text) => demoStore.teamSend(text),
      markTeamRead: () => demoStore.teamRead(),
    };
  }
  const base = (jobId: string, cid: string) => `/api/admin/jobs/${enc(jobId)}/registrations/${enc(cid)}`;
  return {
    addRegistration: (jobId, body) => apiRequest(`/api/admin/jobs/${enc(jobId)}/registrations`, { method: 'POST', body, user }),
    updateCount: (jobId, cid, workerCount) => apiRequest(base(jobId, cid), { method: 'PATCH', body: { workerCount }, user }),
    removeRegistration: (jobId, cid) => apiRequest(base(jobId, cid), { method: 'DELETE', user }),
    tokenAction: (jobId, cid, action) => apiRequest(`${base(jobId, cid)}/token`, { method: 'POST', body: { action }, user }),
    decide: (id, decision) => apiRequest(`/api/admin/requests/${enc(id)}/decide`, { method: 'POST', body: { decision }, user }),
    updateContractor: (cid, body) => apiRequest(`/api/admin/contractors/${enc(cid)}`, { method: 'PATCH', body, user }),
    markRead: (ids) => apiRequest('/api/admin/notifications/read', { method: 'POST', body: ids === 'all' ? { all: true } : { ids }, user }),
    registerDevice: (token) => apiRequest('/api/admin/devices', { method: 'POST', body: { token }, user }),
    unregisterDevice: (token) => apiRequest('/api/admin/devices/remove', { method: 'POST', body: { token }, user }),
    createAnnouncement: (jobId, body) => apiRequest(`/api/admin/jobs/${enc(jobId)}/announcements`, { method: 'POST', body, user }),
    updateAnnouncement: (jobId, id, body) => apiRequest(`/api/admin/jobs/${enc(jobId)}/announcements/${enc(id)}`, { method: 'PATCH', body, user }),
    deleteAnnouncement: (jobId, id) => apiRequest(`/api/admin/jobs/${enc(jobId)}/announcements/${enc(id)}`, { method: 'DELETE', user }),
    sendThreadMessage: (threadId, text) => apiRequest(`/api/admin/threads/${enc(threadId)}/messages`, { method: 'POST', body: { text }, user }),
    markThreadRead: (threadId) => apiRequest(`/api/admin/threads/${enc(threadId)}/read`, { method: 'POST', body: {}, user }),
    sendTeamMessage: (text) => apiRequest('/api/admin/team-chat', { method: 'POST', body: { text }, user }),
    markTeamRead: () => apiRequest('/api/admin/team-chat/read', { method: 'POST', body: {}, user }),
  };
}

/** המזהה של המנהל המחובר לצורך "נקרא על ידי" — במצב תצוגה, מנהל הדוגמה. */
export function readerUid(user: User): string {
  return IS_DEMO ? DEMO_ME.uid : user.uid;
}
